import React, { useState, useEffect, useRef } from "react"
import HomepageLogoLink from "../components/homepage-logo-link"
import { Link, withPrefix } from "gatsby"
import { slugify } from "../helpers/strings"
import menu from "../data/menu.json"
import FocusLock from "react-focus-lock"
import SubNavigation from "../components/sub-navigation"
import MobileSubNavigation from "../components/mobile-sub-navigation"
import { useHoverIntent } from "react-use-hoverintent"

export default function SiteHeader({ path, hash }) {
  const homepageLogoLink = useRef()
  const mobileNavButton = useRef()
  const [mobileNavIsActive, setMobileNavIsActive] = useState(false)

  // Subcomponent to handle Nav Main Item hover intent
  const NavMainItem = ({ item, itemSlug, children }) => {
    const [isHovering, ref] = useHoverIntent({
      timeout: 200,
      sensitivity: 10,
      interval: 100,
    })

    return (
      <li
        ref={ref}
        id={"nav-item-" + itemSlug}
        className={
          "site-header__nav__main__item" +
          (isNavItemActive(item)
            ? " site-header__nav__main__item--active"
            : "") +
          (isHovering ? " site-header__nav__main__item--focus" : "")
        }
        aria-controls={item.children ? "child-item-" + itemSlug : null}
      >
        {children}
      </li>
    )
  }

  const paths = path
    .split("/")
    .filter(function (el) {
      return el !== ""
    })
    .slice(0)
    .slice(-2)

  const getPageParent = () => {
    let parent
    if (
      paths[0] === "2022" ||
      !paths.length ||
      paths[1] === "message-from-the-md"
    ) {
      parent = "intro"
    } else {
      parent = paths[0]
    }
    return parent
  }

  const getPageSlug = () => {
    let slug
    if (paths[0] === "2022") {
      slug = "opening-statement"
    } else {
      slug = paths[1]
    }

    return slug
  }

  // Helper for normalizing paths for correct mobile nav active page underlining, regardless of env
  function normalizePath(path) {
    return path.replace("/external/pubs/ft/ar/2022/", "/")
  }

  function toggleMobileNav() {
    const BODY_LOCK = "util__body-lock"

    if (mobileNavIsActive) {
      setMobileNavIsActive(false)
      document.body.classList.remove(BODY_LOCK)
    } else {
      setMobileNavIsActive(true)
      document.body.classList.add(BODY_LOCK)
    }
  }

  function isNavItemActive(item) {
    if (path.includes("in-focus") && item.name === "In Focus") {
      return true
    } else if (path.includes("what-we-do") && item.name === "What We Do") {
      return true
    } else if (path.includes("who-we-are") && item.name === "Who We Are") {
      return true
    } else if (path.includes("downloads") && item.name === "Downloads") {
      return true
    } else if (
      path.includes("message-from-the-md") &&
      item === "Message from the MD"
    ) {
      return true
    } else {
      return false
    }
  }

  function toggleNavItemOnFocus(e) {
    // If the user is clicking, ignore this focus/blur toggle behavior and allow hover behavior to persist
    if (!document.body.classList.contains("user-is-tabbing")) {
      return
    }

    const focusClass = "site-header__nav__main__item--focus"

    let focusedItemParent = e.target.closest("li.site-header__nav__main__item")

    focusedItemParent.classList.contains(focusClass)
      ? focusedItemParent.classList.remove(focusClass)
      : focusedItemParent.classList.add(focusClass)
  }

  // Simple listener to close overlay on escape key hit
  function handleKeydown(e) {
    if (mobileNavIsActive && e.keyCode === 27) {
      toggleMobileNav()
    }
  }

  useEffect(() => {
    document.addEventListener("keydown", handleKeydown)
    return () => {
      document.removeEventListener("keydown", handleKeydown)
    }
  })

  function getThemeColor(href) {
    if (href.includes("in-focus")) {
      return "theme-color-in-focus"
    } else if (href.includes("what-we-do")) {
      return "theme-color-what-we-do"
    } else if (href.includes("who-we-are")) {
      return "theme-color-who-we-are"
    } else if (
      href.includes("downloads") ||
      href.includes("english") ||
      href.includes("arabic") ||
      href.includes("chinese") ||
      href.includes("french") ||
      href.includes("german") ||
      href.includes("japanese") ||
      href.includes("portuguese") ||
      href.includes("russian") ||
      href.includes("spanish")
    ) {
      return "theme-color-downloads"
    } else if (href.includes("message-from-the-md")) {
      return "theme-color-message-from-the-md"
    } else {
      return null
    }
  }

  return (
    <header className="site-header" id="site-header">
      <div className="site-header__inner">
        <HomepageLogoLink
          passedRef={homepageLogoLink}
          classes="site-header__logo"
          path={path}
        />
        <nav className="site-header__nav">
          <ul className="site-header__nav__main">
            {/* Output letter from MD */}
            <NavMainItem
              item={"Message from the MD"}
              itemSlug={"message-from-the-md"}
            >
              <Link
                to="/message-from-the-md"
                className={getThemeColor("/message-from-the-md")}
                aria-current={
                  path.includes("message-from-the-md") ? "page" : null
                }
              >
                Message from the MD
              </Link>
            </NavMainItem>

            {/* Loop through menu.json and output items */}
            {menu.map((item, index) => {
              let itemSlug = slugify(item.name)

              return item.hideDesktop ? null : (
                <NavMainItem
                  item={item}
                  itemSlug={itemSlug}
                  key={"nav-item-desktop-" + itemSlug}
                >
                  {item.href ? (
                    <Link
                      to={item.href}
                      aria-current={path.includes(item.href) ? "page" : null}
                      className={getThemeColor(item.href)}
                      onFocus={toggleNavItemOnFocus}
                      onBlur={toggleNavItemOnFocus}
                    >
                      {item.name}
                    </Link>
                  ) : (
                    <button
                      type="button"
                      onFocus={toggleNavItemOnFocus}
                      onBlur={toggleNavItemOnFocus}
                    >
                      {item.name}
                    </button>
                  )}
                  <ul
                    id={"child-item-" + itemSlug}
                    className="site-header__nav__sub"
                  >
                    {item.children.map((child, index) => (
                      <li key={"child-item-" + child.name}>
                        {child.href.includes(".pdf") ? (
                          <a
                            href={withPrefix("/") + child.href}
                            aria-current={
                              path.includes(child.href) ? "page" : null
                            }
                            onFocus={toggleNavItemOnFocus}
                            onBlur={toggleNavItemOnFocus}
                          >
                            {child.name}
                          </a>
                        ) : child.href ? (
                          <Link
                            to={child.href}
                            aria-current={
                              path.includes(child.href) ? "page" : null
                            }
                            onFocus={toggleNavItemOnFocus}
                            onBlur={toggleNavItemOnFocus}
                          >
                            {child.name}
                          </Link>
                        ) : (
                          <p>{child.name}</p>
                        )}
                        {child.children ? (
                          <ul className="site-header__nav__super-sub">
                            {child.children.map((subchild, index) => (
                              <li key={"subchild-item-" + subchild.name}>
                                {subchild.href.includes(".pdf") ? (
                                  <a
                                    href={withPrefix("/") + subchild.href}
                                    aria-current={
                                      path.includes(subchild.href)
                                        ? "page"
                                        : null
                                    }
                                    onFocus={toggleNavItemOnFocus}
                                    onBlur={toggleNavItemOnFocus}
                                  >
                                    {subchild.name}
                                  </a>
                                ) : (
                                  <Link
                                    to={subchild.href}
                                    aria-current={
                                      path.includes(subchild.href)
                                        ? "page"
                                        : null
                                    }
                                    onFocus={toggleNavItemOnFocus}
                                    onBlur={toggleNavItemOnFocus}
                                  >
                                    {subchild.name}
                                  </Link>
                                )}
                              </li>
                            ))}
                          </ul>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </NavMainItem>
              )
            })}
          </ul>
        </nav>

        <FocusLock
          disabled={mobileNavIsActive ? false : true}
          shards={
            mobileNavIsActive ? [homepageLogoLink, mobileNavButton] : null
          }
          returnFocus={true}
          noFocusGuards={true}
        >
          <nav
            id="site-mobile-nav"
            className="site-mobile-nav"
            aria-hidden={mobileNavIsActive ? "false" : "true"}
            tabIndex="-1"
          >
            <ul className="site-mobile-nav__main">
              {menu.map((item, index) => {
                return (
                  <li key={"nav-item-" + item.name}>
                    {item.href ? (
                      <Link
                        className={
                          normalizePath(path + hash) ===
                            normalizePath(item.href) &&
                          (path.includes("in-focus") ||
                            path.includes("who-we-are") ||
                            normalizePath(path + hash) === "/")
                            ? "no-color-change"
                            : null
                        }
                        to={item.href}
                        aria-current={
                          normalizePath(path + hash) ===
                          normalizePath(item.href)
                            ? "page"
                            : null
                        }
                        onClick={toggleMobileNav}
                      >
                        {item.name}
                      </Link>
                    ) : (
                      <div>{item.name}</div>
                    )}
                    <ul>
                      {item.children.map((child, index) => (
                        <li key={"child-item-" + child.name}>
                          {child.href.includes(".pdf") ? (
                            <a
                              href={withPrefix("/") + child.href}
                              onClick={toggleMobileNav}
                            >
                              {child.name}
                            </a>
                          ) : (
                              child.href ? (
                                <Link
                              to={child.href}
                              aria-current={
                                normalizePath(path + hash) ===
                                normalizePath(child.href)
                                  ? "page"
                                  : null
                              }
                              onClick={toggleMobileNav}
                            >
                              {child.name}
                            </Link>
                              ) : ( 
                                  <p>{child.name}</p>
                            )
                          )}
                          {child.children ? (
                            <ul className="site-mobile-nav__main__super-sub">
                              {child.children.map((subchild, index) => (
                                <li key={"subchild-item-" + subchild.name}>
                                  {subchild.href.includes(".pdf") ? (
                                    <a
                                      href={withPrefix("/") + subchild.href}
                                      onClick={toggleMobileNav}
                                    >
                                      {subchild.name}
                                    </a>
                                  ) : (
                                    <Link
                                      to={subchild.href}
                                      aria-current={
                                        normalizePath(path + hash) ===
                                        normalizePath(subchild.href)
                                          ? "page"
                                          : null
                                      }
                                      onClick={toggleMobileNav}
                                    >
                                      {subchild.name}
                                    </Link>
                                  )}
                                </li>
                              ))}
                            </ul>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  </li>
                )
              })}
            </ul>
          </nav>
        </FocusLock>

        <button
          className="site-nav__menu-button"
          type="button"
          aria-label="Toggle mobile navigation"
          aria-owns="site-mobile-nav"
          onClick={toggleMobileNav}
          ref={mobileNavButton}
        >
          <span
            className={
              "site-nav__menu-button__icon" +
              (mobileNavIsActive ? " site-nav__menu-button__icon--active" : "")
            }
          ></span>
        </button>
      </div>

      {getPageParent() !== "intro" &&
      getPageParent() !== "downloads" &&
      getPageParent() !== "message-from-the-md" &&
      getPageParent() !== "eng" ? (
        <>
          <SubNavigation id={getPageParent()} current={getPageSlug()} />
          <MobileSubNavigation id={getPageParent()} current={getPageSlug()} />
        </>
      ) : null}
    </header>
  )
}
